import { Injector } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalStorageService } from './localstorage/localstorage.service';
import * as Env from '../../../environments/environment';
export interface HttpJsonBodyOptions {
    headers?: HttpHeaders | Record<string, string | string[]>;
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
    transferCache?: boolean | {
        includeHeaders?: string[];
    };
    timeout?: number;
}
export interface DownloadDocumentoAnotacaoResponse {
    blob: Blob;
    fileName: string;
    contentType: string;
}
export abstract class BaseResourceService<T> {
    private token!: string;
    private uuid!: string;
    private pesquisado: any = [];
    public host: string = Env.environment.service;
    public http: HttpClient;
    public storage: LocalStorageService;
    public timeoutMillis = 2 * 60 * 6000;
    public retryCount = 0;
    constructor(protected injector: Injector) {
        this.http =
            injector.get(HttpClient);
        this.storage =
            injector.get(LocalStorageService);
        this.token =
            this.storage.get('token');
    }
    private obterUuidPesquisado(): string | undefined {
        if (this.pesquisado?.uuid) {
            return String(this.pesquisado.uuid);
        }
        const pesquisadoStorage = sessionStorage.getItem('pesquisado');
        if (!pesquisadoStorage) {
            return undefined;
        }
        try {
            const pesquisado = JSON.parse(pesquisadoStorage);
            if (!pesquisado?.uuid) {
                return undefined;
            }
            this.pesquisado =
                pesquisado;
            return String(pesquisado.uuid);
        }
        catch {
            return undefined;
        }
    }
    get<R = T>({ service, params = {}, uuid = false }: {
        service: string;
        params?: any;
        uuid?: boolean;
    }, options?: HttpJsonBodyOptions): Observable<R> {
        const urlParameters = Object.entries(params)
            .map((entry: any) => entry[1]
            ? entry.join('=')
            : null)
            .filter(Boolean)
            .join('&');
        switch (urlParameters) {
            case '':
                if (uuid) {
                    this.pesquisado =
                        JSON.parse(sessionStorage.getItem('pesquisado') as string);
                    this.uuid =
                        this.pesquisado.uuid;
                    if (!service.includes('?')) {
                        service += '?';
                    }
                    service +=
                        'uuid=' +
                            this.uuid;
                }
                break;
            default:
                if (uuid) {
                    this.pesquisado =
                        JSON.parse(sessionStorage.getItem('pesquisado') as string);
                    this.uuid =
                        this.pesquisado.uuid;
                    if (!service.includes('?')) {
                        service += '?';
                    }
                    service +=
                        urlParameters +
                            '&uuid=' +
                            this.uuid;
                }
                else {
                    if (!service.includes('?')) {
                        service += '?';
                    }
                    service +=
                        urlParameters;
                }
                break;
        }
        return this.http
            .get<R>(service, options)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    getDatalake(service: string, formParams: any, uuid: boolean = false): Observable<any> {
        const datalake = Env.environment.datalake;
        /*
         * Mantido temporariamente porque o Data Lake
         * ainda utiliza o token SSC em contrato próprio.
         */
        const tokenSSC = this.storage.get('token');
        if (!datalake?.datalakeService) {
            console.warn('Datalake service não configurado para este ambiente.');
            return of(null);
        }
        const url = `${datalake.datalakeService}${service}`;
        if (uuid &&
            this.pesquisado?.uuid) {
            formParams.uuid =
                this.pesquisado.uuid;
        }
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json; charset=UTF-8',
                Authorization: `Bearer ${datalake.token}`,
                token: `Bearer ${tokenSSC}`
            }),
            params: formParams
        };
        return this.http.get<any>(url, httpOptions);
    }
    post<R = T>(objeto: any, service: string, uuid: boolean = false, options?: HttpJsonBodyOptions): Observable<R> {
        let payload = objeto;
        if (uuid) {
            const uuidPesquisado = this.obterUuidPesquisado();
            if (uuidPesquisado) {
                /*
                 * FormData não pode ser convertido utilizando
                 * object spread.
                 *
                 * Os campos internos do multipart não são
                 * propriedades enumeráveis e seriam perdidos.
                 *
                 * Por isso, quando o corpo for FormData,
                 * é criado um novo multipart preservando todos
                 * os campos recebidos e adicionando o UUID.
                 *
                 * O FormData original não é alterado.
                 */
                if (objeto instanceof FormData) {
                    const multipart = new FormData();
                    objeto.forEach((valor: FormDataEntryValue, chave: string) => {
                        multipart.append(chave, valor);
                    });
                    multipart.set('uuid', uuidPesquisado);
                    payload =
                        multipart;
                }
                else {
                    /*
                     * Para objetos comuns, mantém o
                     * comportamento existente.
                     *
                     * Também cria um novo objeto para não
                     * modificar o parâmetro original.
                     */
                    payload = {
                        ...objeto,
                        uuid: uuidPesquisado
                    };
                }
            }
        }
        return this.http
            .post<R>(service, payload, options)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    deleteId<R = T>(service: string, params: any = {}, uuid: boolean = false) {
        /*
         * Cria um novo objeto para não alterar
         * os parâmetros fornecidos pelo consumidor.
         */
        const parametrosRequisicao = {
            ...params
        };
        if (uuid) {
            const uuidPesquisado = this.obterUuidPesquisado();
            /*
             * O UUID somente é incluído quando estiver
             * efetivamente disponível.
             *
             * Isso evita enviar uuid=undefined.
             */
            if (uuidPesquisado) {
                parametrosRequisicao.uuid =
                    uuidPesquisado;
            }
        }
        const urlParameters = Object.entries(parametrosRequisicao)
            .filter(([, valor]) => valor !== null &&
            valor !== undefined &&
            valor !== '')
            .map(([chave, valor]) => `${encodeURIComponent(chave)}` +
            `=${encodeURIComponent(String(valor))}`)
            .join('&');
        let url = service;
        if (urlParameters) {
            /*
             * Preserva URLs que já possuam query string.
             */
            const separador = !url.includes('?')
                ? '?'
                : (url.endsWith('?') ||
                    url.endsWith('&'))
                    ? ''
                    : '&';
            url +=
                separador +
                    urlParameters;
        }
        return this.http
            .delete<R>(url)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    upload(url: string, objeto: any, token: boolean = true) {
        const formData = new FormData();
        formData.append('arquivo', objeto.arquivo);
        formData.append('nome_admin', objeto.nome_admin);
        formData.append('email', objeto.email);
        formData.append('tipo', objeto.tipo);
        return this.http
            .post(url, formData)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    uploadDocumentoAnotacao(url: string, payload: {
        documento: File;
        acesso: string;
        tipo_documento_id: number | string;
        anotacao_id: number | string;
    }, uuid: string) {
        const formData = new FormData();
        formData.append('documento', payload.documento, payload.documento.name);
        formData.append('acesso', payload.acesso);
        formData.append('tipo_documento_id', String(payload.tipo_documento_id));
        formData.append('anotacao_id', String(payload.anotacao_id));
        /*
         * Mantido conforme contrato atual do upload.
         * O token é obtido no momento da chamada,
         * evitando utilizar valor antigo do construtor.
         */
        const tokenAtual = this.storage.get('token');
        const headers = new HttpHeaders({
            Authorization: `Bearer ${tokenAtual}`
        });
        const uuidPesquisa = uuid ||
            this.pesquisado?.uuid ||
            '';
        const params = new HttpParams()
            .set('uuid', uuidPesquisa);
        return this.http
            .post(`${this.host}${url}`, formData, {
            headers,
            params
        })
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    /**
     * Baixa um documento protegido por autenticação.
     *
     * A requisição utiliza HttpClient e passa pelo
     * interceptor do Angular. Não é utilizado
     * window\.open diretamente na URL protegida.
     *
     * O retorno contém:
     * - Blob do documento;
     * - nome obtido do Content-Disposition;
     * - Content-Type retornado pelo backend.
     */
    downloadDocumentoAnotacao(url: string): Observable<DownloadDocumentoAnotacaoResponse> {
        const urlNormalizada = (url ?? '')
            .trim()
            .replace(/^Http:\/\//, 'http://')
            .replace(/^Https:\/\//, 'https://');
        if (!urlNormalizada) {
            return throwError(() => new Error('URL do documento não informada.'));
        }
        /*
         * A URL sempre passa pela normalização.
         *
         * Isso corrige URLs absolutas retornadas pelo
         * backend com host ou porta incompatíveis com
         * o ambiente atual.
         */
        const urlDownload = this.montarUrlDownloadDocumento(urlNormalizada);
        return this.http
            .get(urlDownload, {
            responseType: 'blob',
            observe: 'response',
            withCredentials: true
        })
            .pipe(map((response: HttpResponse<Blob>): DownloadDocumentoAnotacaoResponse => {
            const blob = response.body ??
                new Blob();
            const contentDisposition = response.headers.get('Content-Disposition');
            const fileName = this.obterNomeArquivo(contentDisposition, urlDownload);
            const contentType = response.headers.get('Content-Type') ||
                blob.type ||
                'application/octet-stream';
            return {
                blob,
                fileName,
                contentType
            };
        }), catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    private montarUrlDownloadDocumento(url: string): string {
        const hostSemBarra = this.host.replace(/\/+$/, '');
        let caminho = url.trim();
        if (/^https?:\/\//i.test(caminho)) {
            try {
                const urlAbsoluta = new URL(caminho);
                caminho =
                    `${urlAbsoluta.pathname}` +
                        `${urlAbsoluta.search}` +
                        `${urlAbsoluta.hash}`;
            }
            catch {
                /*
                 * Mantém o texto recebido para o
                 * tratamento de caminho abaixo.
                 */
            }
        }
        if (!caminho.startsWith('/')) {
            caminho =
                `/${caminho}`;
        }
        if (caminho === '/api/v1' ||
            caminho.startsWith('/api/v1/')) {
            caminho =
                caminho.replace(/^\/api\/v1/, '');
        }
        if (!caminho.startsWith('/')) {
            caminho =
                `/${caminho}`;
        }
        return (`${hostSemBarra}` +
            `${caminho}`);
    }
    private obterNomeArquivo(contentDisposition: string | null, urlDownload: string): string {
        if (contentDisposition) {
            const nomeUtf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i
                .exec(contentDisposition);
            if (nomeUtf8?.[1]) {
                return this.decodificarNomeArquivo(nomeUtf8[1]
                    .trim()
                    .replace(/^["']|["']$/g, ''));
            }
            const nomePadrao = /filename\s*=\s*["']?([^;"']+)["']?/i
                .exec(contentDisposition);
            if (nomePadrao?.[1]) {
                return this.decodificarNomeArquivo(nomePadrao[1]
                    .trim());
            }
        }
        const urlSemParametros = urlDownload
            .split('?')[0]
            .split('#')[0];
        const ultimoSegmento = urlSemParametros
            .split('/')
            .filter(Boolean)
            .pop();
        return ultimoSegmento
            ? this.decodificarNomeArquivo(ultimoSegmento)
            : 'documento';
    }
    private decodificarNomeArquivo(nomeArquivo: string): string {
        try {
            return decodeURIComponent(nomeArquivo);
        }
        catch {
            return nomeArquivo;
        }
    }
    upload2(file: File) {
        const formData = new FormData();
        const path = '/empresa-2pda/importar';
        formData.append('arquivo', file);
        formData.append('token', this.token);
        const headers = new HttpHeaders({
            'Content-Type': 'multipart/form-data'
        });
        const params = new HttpParams()
            .append('token', this.token);
        const req = new HttpRequest('POST', this.host + path, formData, {
            headers,
            params,
            reportProgress: true,
            responseType: 'json'
        });
        return this.http.request(req);
    }
    put<R = T>(objeto: any, service: string) {
        return this.http
            .put<R>(service, objeto)
            .pipe(catchError((error: HttpErrorResponse) => this.handleError(error)));
    }
    protected handleError(error: HttpErrorResponse) {
        /*
         * O erro precisa chegar ao subscribe.error
         * do componente.
         *
         * O interceptor continua responsável pelo
         * tratamento global de 401/403.
         */
        return throwError(() => error);
    }
}
